import { colors } from 'fitify-ui'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { Body } from '@/components/exercises/Exercise/Muscles/MuscleMap/Body'
import type { ExerciseMuscleIntensity } from '@/utils/exercises'

import {
  BACK_GRAY_DETAILS,
  BACK_LIGHT_DETAILS,
  BACK_MUSCLE_REGIONS,
  BACK_OUTLINE,
  FRONT_GRAY_DETAILS,
  FRONT_LIGHT_DETAILS,
  FRONT_MUSCLE_REGIONS,
  FRONT_OUTLINE,
  VIEWPORT_HEIGHT,
  VIEWPORT_WIDTH,
} from './MuscleMap.Utils'

const GAP = 30
const SVG_WIDTH = VIEWPORT_WIDTH * 2 + GAP
const SVG_HEIGHT = VIEWPORT_HEIGHT + 24 // Extra for labels
const BACK_OFFSET_X = VIEWPORT_WIDTH + GAP

type Props = {
  intensity?: ExerciseMuscleIntensity
  intensityStretch?: ExerciseMuscleIntensity
}

const StyledSvg = styled.svg`
  width: 100%;
  max-width: 310px;
  height: auto;
`

export const MuscleMap = ({ intensity, intensityStretch }: Props) => {
  const { t } = useTranslation()

  return (
    <StyledSvg
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Muscle activation diagram"
    >
      {/* ── Front body ───────────────────────────────────────────── */}
      <Body
        grayDetailsPath={FRONT_GRAY_DETAILS}
        intensity={intensity}
        intensityStretch={intensityStretch}
        lightDetailsPath={FRONT_LIGHT_DETAILS}
        muscleRegions={FRONT_MUSCLE_REGIONS}
        outlinePath={FRONT_OUTLINE}
      />

      {/* ── Back body ────────────────────────────────────────────── */}
      <Body
        grayDetailsPath={BACK_GRAY_DETAILS}
        groupTransform={`translate(${BACK_OFFSET_X}, 0)`}
        intensity={intensity}
        intensityStretch={intensityStretch}
        lightDetailsPath={BACK_LIGHT_DETAILS}
        muscleRegions={BACK_MUSCLE_REGIONS}
        outlinePath={BACK_OUTLINE}
      />

      {/* ── Labels ────────────────────────────────────────────────── */}
      <text
        x={VIEWPORT_WIDTH / 2}
        y={VIEWPORT_HEIGHT + 18}
        textAnchor="middle"
        fontSize={16}
        fontWeight="600"
        fill={colors.Gray600}
      >
        {t('landing_exercises_muscle_front')}
      </text>
      <text
        x={BACK_OFFSET_X + VIEWPORT_WIDTH / 2}
        y={VIEWPORT_HEIGHT + 18}
        textAnchor="middle"
        fontSize={16}
        fontWeight="600"
        fill={colors.Gray600}
      >
        {t('landing_exercises_muscle_back')}
      </text>
    </StyledSvg>
  )
}
